import { h } from '@/components/dom';
import { checkbox, note, section, slider } from '@/components/controls';
import { grayToImageData } from '@/image/decode';
import type { App } from './App';

export function createTargetPanel(app: App): HTMLElement {
  const pre = () => app.state.config.preprocess;
  const brightness = slider({ label: 'Brightness', min: -1, max: 1, step: 0.01, value: pre().brightness, onInput: (v) => app.updateConfig((c) => (c.preprocess.brightness = v)) });
  const contrast = slider({ label: 'Contrast', min: 0, max: 4, step: 0.01, value: pre().contrast, onInput: (v) => app.updateConfig((c) => (c.preprocess.contrast = v)) });
  const gamma = slider({ label: 'Gamma', min: 0.2, max: 4, step: 0.01, value: pre().gamma, onInput: (v) => app.updateConfig((c) => (c.preprocess.gamma = v)) });
  const blur = slider({ label: 'Blur σ', min: 0, max: 12, step: 0.1, value: pre().blurSigma, format: (v) => `${v.toFixed(1)} px`, onInput: (v) => app.updateConfig((c) => (c.preprocess.blurSigma = v)) });
  const sharpen = slider({ label: 'Sharpen', min: 0, max: 4, step: 0.05, value: pre().sharpen, onInput: (v) => app.updateConfig((c) => (c.preprocess.sharpen = v)) });
  const edge = slider({ label: 'Edge enhance', min: 0, max: 4, step: 0.05, value: pre().edgeEnhance, onInput: (v) => app.updateConfig((c) => (c.preprocess.edgeEnhance = v)) });
  const edgeWeight = slider({ label: 'Edge importance', min: 0, max: 10, step: 0.1, value: pre().edgeWeight, hint: 'Extra scoring weight around edges (edge-aware error).', onInput: (v) => app.updateConfig((c) => (c.preprocess.edgeWeight = v)) });
  const thresholdOn = checkbox({ label: 'Threshold (black & white)', value: pre().threshold !== null, onChange: (on) => app.updateConfig((c) => (c.preprocess.threshold = on ? (threshold.valueOr(0.5)) : null)) });
  const thresholdSlider = slider({ label: 'Threshold level', min: 0, max: 1, step: 0.01, value: pre().threshold ?? 0.5, onInput: (v) => app.updateConfig((c) => (c.preprocess.threshold = v)) });
  const threshold = { valueOr: (d: number) => pre().threshold ?? d };
  const invert = checkbox({ label: 'Invert', value: pre().invert, onChange: (v) => app.updateConfig((c) => (c.preprocess.invert = v)) });
  const normalize = checkbox({ label: 'Normalize range', value: pre().normalize, onChange: (v) => app.updateConfig((c) => (c.preprocess.normalize = v)) });
  const maskOn = checkbox({ label: 'Mask outside the frame', value: app.state.config.frameMask.enabled, onChange: (v) => app.updateConfig((c) => (c.frameMask.enabled = v)) });
  const outside = slider({ label: 'Outside weight', min: 0, max: 1, step: 0.05, value: app.state.config.frameMask.outsideWeight, hint: 'How much strings crossing empty space outside a polygon frame are penalized.', onInput: (v) => app.updateConfig((c) => (c.frameMask.outsideWeight = v)) });

  const preview = h('canvas', { class: 'preview-canvas', width: 256, height: 256 });
  const previewLabel = h('div', { class: 'preview-label' }, 'Processed target');
  let lastProcessed: Uint8Array | null = null;
  const drawPreview = (gray: Uint8Array | null, w: number, hgt: number) => {
    if (gray === lastProcessed) return;
    lastProcessed = gray;
    const ctx = preview.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, preview.width, preview.height);
    if (!gray || w === 0) return;
    const tmp = document.createElement('canvas');
    tmp.width = w;
    tmp.height = hgt;
    tmp.getContext('2d')!.putImageData(grayToImageData(gray, w, hgt), 0, 0);
    const scale = Math.min(preview.width / w, preview.height / hgt);
    const dw = w * scale;
    const dh = hgt * scale;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(tmp, (preview.width - dw) / 2, (preview.height - dh) / 2, dw, dh);
  };

  app.store.subscribe((s, p) => {
    if (s.config !== p.config || s === p) {
      const q = s.config.preprocess;
      brightness.setValue(q.brightness);
      contrast.setValue(q.contrast);
      gamma.setValue(q.gamma);
      blur.setValue(q.blurSigma);
      sharpen.setValue(q.sharpen);
      edge.setValue(q.edgeEnhance);
      edgeWeight.setValue(q.edgeWeight);
      thresholdOn.setValue(q.threshold !== null);
      thresholdSlider.setDisabled(q.threshold === null);
      if (q.threshold !== null) thresholdSlider.setValue(q.threshold);
      invert.setValue(q.invert);
      normalize.setValue(q.normalize);
      maskOn.setValue(s.config.frameMask.enabled);
      outside.setValue(s.config.frameMask.outsideWeight);
    }
    if (s.target !== p.target || s === p) drawPreview(s.target.processed, s.target.width, s.target.height);
  }, { immediate: true });

  return h(
    'div',
    null,
    section('Preview', [preview, previewLabel]),
    section('Tone', [brightness.element, contrast.element, gamma.element, invert.element, normalize.element]),
    section('Detail', [blur.element, sharpen.element, edge.element, edgeWeight.element, thresholdOn.element, thresholdSlider.element]),
    section('Frame mask', [maskOn.element, outside.element, note('Pixels outside the pin frame are ignored (or penalized) by the optimizer.')]),
  );
}
